import React, { useMemo, useRef } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { InfoIcon } from 'icons/materialIcons';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip as ChartTooltip,
    Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    ChartTooltip,
    Legend
);

const ViewsOverTime = ({ data = [] }) => {
    const chartRef = useRef();

    // Add debugging to understand the data inconsistency
    console.group('🔍 ViewsOverTime - Data Analysis Debug');
    console.log('📊 Raw data received:', data);
    console.log('📊 Data type:', typeof data);
    console.log('📊 Is array:', Array.isArray(data));
    if (data && typeof data === 'object' && !Array.isArray(data)) {
        console.log('📊 Data structure:', {
            hasByType: !!data.byType,
            hasOverTime: !!data.overTime,
            byTypeData: data.byType,
            overTimeLength: data.overTime ? data.overTime.length : 0,
            sampleOverTime: data.overTime ? data.overTime.slice(0, 3) : []
        });
    }
    console.groupEnd();

    // Helper function to safely format dates
    const formatDate = (dateInput) => {
        if (!dateInput) return '';

        try {
            const date = new Date(dateInput);
            if (isNaN(date.getTime())) {
                return 'Invalid Date';
            }
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            console.error('Error formatting date:', error, dateInput);
            return 'Invalid Date';
        }
    };

    // Create default data for empty state - with view types
    const getDefaultData = () => {
        const now = new Date();
        const defaultData = [];

        for (let i = 29; i >= 0; i--) {
            const date = new Date();
            date.setDate(now.getDate() - i);
            defaultData.push({
                date: formatDate(date),
                accessory: 0,
                qr_code: 0,
                share_link: 0,
                total: 0
            });
        }

        return defaultData;
    };    // Process raw data by view type - FIXED: Consistent source type normalization
    const processRawData = (rawData) => {
        if (rawData.length > 0 && (rawData[0].sourceType || rawData[0].timestamp)) {
            const dailyData = new Map();

            rawData.forEach(item => {
                const dateStr = formatDate(item.timestamp || item.date || item.createdAt);
                if (!dateStr || dateStr === 'Invalid Date') return;

                if (!dailyData.has(dateStr)) {
                    dailyData.set(dateStr, {
                        date: dateStr,
                        accessory: 0,
                        qr_code: 0,
                        share_link: 0,
                        total: 0
                    });
                }

                const entry = dailyData.get(dateStr);
                // FIXED: Use consistent source type normalization
                let sourceType = item.sourceType || item.source || 'share_link';

                // Normalize source types to match ViewTypeBreakdown
                if (sourceType === 'website' || sourceType === 'direct') {
                    sourceType = 'share_link';
                } else if (sourceType === 'nfc' || sourceType === 'tag') {
                    sourceType = 'accessory';
                }

                if (sourceType === 'accessory') {
                    entry.accessory += 1;
                } else if (sourceType === 'qr_code') {
                    entry.qr_code += 1;
                } else if (sourceType === 'share_link') {
                    entry.share_link += 1;
                }
                entry.total += 1;
            });

            return Array.from(dailyData.values()).sort((a, b) => {
                return new Date(a.date) - new Date(b.date);
            });
        }

        return rawData;
    };

    // Convert byType counts to chart data
    const createDataFromCounts = (byType = {}) => {
        const now = new Date();
        const dateStr = formatDate(now);

        return [{
            date: dateStr,
            accessory: byType.accessory || 0,
            qr_code: byType.qr_code || 0,
            share_link: byType.share_link || 0,
            total: (byType.accessory || 0) + (byType.qr_code || 0) + (byType.share_link || 0)
        }];
    };    // Get chart data with fallbacks
    const chartData = useMemo(() => {
        // If nothing provided, return default empty data
        if (!data || (Array.isArray(data) && data.length === 0)) {
            return getDefaultData();
        }

        // FIXED: Always prioritize byType data when available for consistency with ViewTypeBreakdown
        if (data.byType && !Array.isArray(data)) {
            // Use byType for consistency with ViewTypeBreakdown component
            return createDataFromCounts(data.byType);
        }

        return processRawData(data);
    }, [data]);

    // Calculate totals for all view types
    const totals = useMemo(() => {
        if (!chartData || chartData.length === 0) {
            return {
                total: 0,
                accessory: 0,
                qr_code: 0,
                share_link: 0
            };
        }

        return chartData.reduce((acc, item) => {
            acc.total += item.total || 0;
            acc.accessory += item.accessory || 0;
            acc.qr_code += item.qr_code || 0;
            acc.share_link += item.share_link || 0;
            return acc;
        }, {
            total: 0,
            accessory: 0,
            qr_code: 0,
            share_link: 0
        });
    }, [chartData]);

    // Prepare Chart.js data with bar chart configuration for each view type
    const chartJSData = {
        labels: chartData.map(item => item.date),
        datasets: [
            {
                label: 'Accessory',
                data: chartData.map(item => item.accessory || 0),
                backgroundColor: '#3ABFF5',
                borderColor: '#3ABFF5',
                borderWidth: 1,
                barThickness: 2,
                maxBarThickness: 2,
                categoryPercentage: 0.3,
                barPercentage: 0.3,
            },
            {
                label: 'QR Code',
                data: chartData.map(item => item.qr_code || 0),
                backgroundColor: '#8CF1D9',
                borderColor: '#8CF1D9',
                borderWidth: 1,
                barThickness: 2,
                maxBarThickness: 2,
                categoryPercentage: 0.3,
                barPercentage: 0.3,
            }, {
                label: 'Share',
                data: chartData.map(item => item.share_link || 0),
                backgroundColor: '#EEA5F5',
                borderColor: '#EEA5F5',
                borderWidth: 1,
                barThickness: 2,
                maxBarThickness: 2,
                categoryPercentage: 0.3,
                barPercentage: 0.3,
            }
        ]
    };

    // Chart options
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                enabled: true,
                mode: 'index',
                intersect: false,
            }
        },
        scales: {
            x: {
                stacked: false,
                grid: {
                    display: true,
                    drawOnChartArea: true,
                    color: 'rgba(0, 0, 0, 0.1)'
                },
                ticks: {
                    maxRotation: 45,
                    color: '#4f4f4f',
                    font: { size: 12 }
                }
            },
            y: {
                stacked: false,
                beginAtZero: true,
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)'
                },
                ticks: {
                    stepSize: 1,
                    precision: 0
                }
            }
        }
    }; return (
        <Box
            sx={{
                width: '100%',
                height: '414px',
                position: 'relative',
                borderRadius: '18px',
                backgroundColor: '#FFFFFF'
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    padding: '32px 18px 26px 18px',
                    flexDirection: 'column'
                }}
            >
                {/* Header section */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        color: '#333333',
                        fontSize: '12px',
                        minWidth: '240px'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', columnGap: '5px' }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
                            Views Over Time
                        </Typography>
                        <Tooltip title="Profile views over time broken down by source type">
                            <InfoIcon sx={{ fontSize: 16, color: '#828282' }} />
                        </Tooltip>
                    </Box>
                </Box>

                {/* Legend */}
                <Box
                    sx={{
                        display: 'flex',
                        padding: '20px 0',
                        gap: '15px',
                        flexWrap: 'wrap'
                    }}
                >                    {[
                    { label: 'Accessory', value: totals.accessory, color: '#3ABFF5' },
                    { label: 'QR Code', value: totals.qr_code, color: '#8CF1D9' },
                    { label: 'Share', value: totals.share_link, color: '#EEA5F5' }
                ].map((item, index) => (
                    <Box
                        key={index}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        <Box
                            sx={{
                                width: '16px',
                                height: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: item.color,
                                borderRadius: '4px'
                            }}
                        >
                            <svg width="16" height="14" viewBox="0 0 8 6" fill="none">
                                <path d="M1 3L3 5L7 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </Box>
                        <Typography sx={{ fontSize: '13px', color: '#4f4f4f' }}>
                            {item.label} ({item.value})
                        </Typography>
                    </Box>
                ))}
                </Box>

                {/* Chart */}
                <Box
                    sx={{
                        height: '245px',
                        width: '100%',
                        marginTop: '20px'
                    }}
                >
                    <Bar
                        ref={chartRef}
                        data={chartJSData}
                        options={options}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default ViewsOverTime;